// An icon's SVG, drawn inline (so `currentColor` takes the chosen colour) with
// everything that can run taken out. Login review, 2026-10-01: this text comes
// from places a stranger can write (album pages are saved through the public
// order-id route; the icon catalogue by settings.manage), and it used to be
// set with dangerouslySetInnerHTML — in the editor that admins and agents open
// on customers' albums, with their session in localStorage.
//
// How: the XML parser (never the HTML one), an ALLOWLIST of drawing elements
// and attributes, and the cleaned NODES inserted as they are. Nothing is
// turned back into markup for the HTML parser to re-read — that round trip is
// where sanitizer bypasses live. A real icon draws exactly as before.
// Guard: SafeSvg.test.tsx, lib/innerHtmlSinks.guard.test.ts
import { useCallback, useLayoutEffect, useRef, type CSSProperties } from 'react';

const SVG_NS = 'http://www.w3.org/2000/svg';

/** The drawing vocabulary an icon may use; anything else goes, subtree and all
 *  (script, foreignObject, a, image, animate, set, iframe…). */
const ELEMENTS = new Set([
  'svg', 'g', 'defs', 'symbol', 'use', 'title', 'desc', 'style', 'switch', 'image',
  'path', 'circle', 'ellipse', 'line', 'polyline', 'polygon', 'rect',
  'text', 'tspan', 'textPath',
  'linearGradient', 'radialGradient', 'stop', 'pattern', 'clipPath', 'mask', 'marker',
  'filter', 'feBlend', 'feColorMatrix', 'feComponentTransfer', 'feComposite', 'feDropShadow',
  'feFlood', 'feFuncA', 'feFuncB', 'feFuncG', 'feFuncR', 'feGaussianBlur', 'feMerge',
  'feMergeNode', 'feMorphology', 'feOffset',
]);

/** A reference to anything but a fragment of this same SVG (`url(#id)`). */
const OUTSIDE_URL = /url\(\s*(?!['"]?\s*#)/i;

/** An embedded picture (`data:image/…`) — drawn as an image, so it cannot run. */
const isDataImage = (v: string) => /^data:image\//i.test(v.trim());
const hrefOf = (el: Element) => [...el.attributes].find((a) => a.localName === 'href')?.value ?? '';

function cleanAttributes(el: Element): void {
  for (const attr of [...el.attributes]) {
    const name = attr.localName.toLowerCase();
    const squashed = attr.value.replace(/[\u0000- ]+/g, '').toLowerCase();
    const localHref = attr.value.trim().startsWith('#')
      || (el.localName === 'image' && isDataImage(attr.value));
    const drop = name.startsWith('on')                                   // onload, onclick…
      || (name === 'href' && !localHref)                                 // href / xlink:href
      || /(?:java|vb)script:|data:text\/html/.test(squashed)
      || OUTSIDE_URL.test(attr.value);
    if (drop) el.removeAttributeNode(attr);
  }
}

/** Plain CSS stays; anything that loads, runs, or smuggles markup goes. */
function cleanStyleElement(el: Element): boolean {
  const css = el.textContent ?? '';
  if (/[<>]|@import|(?:java|vb)script:|expression\s*\(/i.test(css) || OUTSIDE_URL.test(css)) return false;
  el.textContent = css;   // CDATA or not, one plain text node
  return true;
}

function clean(el: Element): void {
  for (const child of [...el.children]) {
    // A link around part of the drawing: keep the drawing, drop the link.
    if (child.namespaceURI === SVG_NS && child.localName === 'a') {
      const inner = [...child.childNodes];
      child.replaceWith(...inner);
      for (const n of inner) if (n.nodeType === 1) cleanOne(n as Element, el);
      continue;
    }
    cleanOne(child, el);
  }
  cleanAttributes(el);
}

function cleanOne(child: Element, parent: Element): void {
  const allowed = child.namespaceURI === SVG_NS && ELEMENTS.has(child.localName)
    && (child.localName !== 'style' || cleanStyleElement(child))
    && (child.localName !== 'image' || isDataImage(hrefOf(child)));
  if (!allowed) { if (child.parentNode === parent) child.remove(); return; }
  clean(child);
}

/** Strict SVG first; an SVG pasted with HTML habits (unquoted values,
 *  &nbsp;) through the HTML parser. Either way only the parsed TREE is used. */
function parseSvg(text: string): Element | null {
  let xml = text;
  // Pasted without xmlns it would parse outside the SVG namespace and not draw.
  if (/^<svg\b/i.test(xml) && !/^<svg\b[^>]*\sxmlns\s*=/i.test(xml)) {
    xml = xml.replace(/^<svg\b/i, `<svg xmlns="${SVG_NS}"`);
  }
  try {
    const parsed = new DOMParser().parseFromString(xml, 'image/svg+xml');
    const root = parsed.documentElement;
    if (root && root.namespaceURI === SVG_NS && root.localName === 'svg'
        && parsed.getElementsByTagNameNS('*', 'parsererror').length === 0) return root;
  } catch { /* fall through to the HTML parser */ }
  try {
    // A DOMParser document has no browsing context: nothing in it loads or runs.
    const found = new DOMParser().parseFromString(text, 'text/html').body.querySelector('svg');
    return found && found.namespaceURI === SVG_NS ? found : null;
  } catch {
    return null;
  }
}

/** The cleaned <svg>, ready to insert into `doc` — or null if it is not one. */
export function safeSvgElement(svg: string, doc: Document = document): SVGSVGElement | null {
  if (typeof svg !== 'string' || !svg.trim()) return null;
  const root = parseSvg(svg.trim());
  if (!root) return null;
  clean(root);
  return doc.importNode(root, true) as unknown as SVGSVGElement;
}

interface SafeSvgProps {
  svg: string;
  as?: 'span' | 'div';
  className?: string;
  style?: CSSProperties;
  title?: string;
}

/** The host element with the cleaned <svg> as its only child. */
export function SafeSvg({ svg, as = 'span', className, style, title }: SafeSvgProps) {
  const host = useRef<HTMLElement | null>(null);
  const setHost = useCallback((node: HTMLElement | null) => { host.current = node; }, []);
  useLayoutEffect(() => {
    const el = host.current;
    if (!el) return;
    const drawn = safeSvgElement(svg, el.ownerDocument);
    el.replaceChildren(...(drawn ? [drawn] : []));
  }, [svg]);
  return as === 'div'
    ? <div ref={setHost} className={className} style={style} title={title} />
    : <span ref={setHost} className={className} style={style} title={title} />;
}
